import React, { useState, useEffect, useRef, act } from "react";
import { Navigate } from "react-router-dom";
import Grid from "@mui/material/Grid2";

import { 
  Campaign, 
  CampaignChar, 
  Character,
} from "@dndModels";
import API from "@dndUtilities/system_api";
import DandDCharacterUtilities from "@dndUtilities/char_utilities";
import { makeStyles } from "@utilities/theme_utilities";

import DandDButtonBox from "@dndComponents/DandDButtonBoxSimple";
import CharConnectUtilities from "@dndUtilities/char_connect_utilities";
import CampaignCharacterInput from "./CampaignCharacterInput";
import FlexBox from "@components/layout/FlexBox";
import DisplayObject from "@dndComponents/display/DisplayObject";
import AddButton from "@components/input/buttons/icon_buttons/AddButton";
import ModalOrDrawer from "@components/display/ModalOrDrawer";
import ToggleButtonBox from "@components/input/buttons/ToggleButtonBox";
import RefreshButton from "@components/input/buttons/icon_buttons/RefreshButton";

const api = API.getInstance();
const char_util = DandDCharacterUtilities.getInstance();
const connect_util = CharConnectUtilities.getInstance();

interface Props {
  campaign: Campaign;
}

export default function CampaignCharactersInput({ campaign }: Props) {
  const [reloading, setReloading] = useState(false);
  const [processing, setProcessing] = useState(false);
  const [navigateTo, setNavigateTo] = useState<string | null>(null);
  const [my_characters, setMyCharacters] = useState<Character[]>([]);
  const [adding, setAdding] = useState(false);
  const [add_existing, setAddExisting] = useState(false);
  const [sidekick, setSidekick] = useState(false);
  const [unassigned, setUnassigned] = useState(false);
  const [hidden, setHidden] = useState(false);
  const [details_hidden, setDetailsHidden] = useState(false);
  const mounted = useRef(false);

  useEffect(() => { 
    mounted.current = true;

    return () => {
      mounted.current = false;
    };
  }, []);
    
  useEffect(() => {
    if (reloading) {
      setReloading(false);
    }
  }, [reloading, setReloading]);
  
  const useStyles = makeStyles()((theme) => {
    return ({
      root: {
        backgroundColor: theme.palette.secondary.main,
        border: `1px solid ${theme.palette.divider}`,
        borderRadius: '4px',
        marginBottom: '4px',
      },
    });
  });

  const { classes, cx } = useStyles();

  const renderCharacters = (active: boolean) => {
    let c_chars = campaign.c_chars.filter(o => o.active === active);
    // debugger;
    if (campaign.owner_id !== api.user_id) {
      c_chars = c_chars.filter(o => !o.hide_char || (o.char && o.char.owner_id === api.user_id));
    } 
    return c_chars.map(c_char => {
      return (
        <Grid size={{ xs: 12, sm: 6, md: 4, lg: 3 }} key={c_char._id}>
          <CampaignCharacterInput
            c_char={c_char} 
            campaign={campaign} 
            // updateCampaign={() => { setReloading(true); }}
            onUpdated={() => { setReloading(true); }}
          />
        </Grid>
      );
    })
  }

  const renderExtras = () => {
    return (
      <ModalOrDrawer 
        open={adding} 
        onClose={() => setAdding(false) }>
        <FlexBox direction="column">
          <FlexBox direction="row">
            <FlexBox>
              <ToggleButtonBox name="Add Existing Character" 
                disabled={ processing || !api.online || unassigned } 
                value={add_existing}
                onToggle={ async () => {
                  if (my_characters.length === 0 && api.user) {
                    if (api.user.my_characters.length === 0) {
                      setProcessing(true);
                      api.user.my_characters = await api.getPlayerObjects<Character>({ data_type: "character" });
                      setProcessing(false);
                    }
                    // debugger;
                    const campaign_char_ids = campaign.characters.map(o => o._id);
                    const active_chars = api.user.my_characters.filter(o => !o.is_archived && !campaign_char_ids.includes(o._id));
                    setMyCharacters(active_chars);
                  }
                  setAddExisting(!add_existing);
                }}
              />
            </FlexBox>
            <FlexBox>
              <DandDButtonBox name="Create New Character" 
                disabled={ processing || !api.online } 
                onClick={ async () => {
                  if (api.user) {
                    setProcessing(true);
                    const new_char = new Character();
                    new_char.owner_id = unassigned ? "" : api.user._id;
                    new_char.old_campaign_id = new_char._id;
                    new_char.spirit.sidekick = sidekick;
                    connect_util.connectCampaign2(new_char, campaign);
                    new_char.last_campaign_update = campaign.last_updated;
                    await char_util.enforceOptions(new_char, campaign);
                    const res = await api.createCharacter(new_char);
                    if (res && res.body.id) {
                      api.user.my_characters.push(new_char);
                      const c_char = new CampaignChar();
                      c_char.campaign_id = campaign._id;
                      c_char.campaign = campaign;
                      c_char.char_id = new_char._id;
                      c_char.char = new_char;
                      c_char.hide_char = hidden;
                      c_char.hide_details = details_hidden;
                      await api.upsertObject("campaign_char", c_char);
                      campaign.c_chars.push(c_char);
                      new_char.c_char = c_char;
                      campaign.characters.push(new_char);
                    } else {
                      console.log(res);
                    }
                    setProcessing(false);
                  }
                }}
              />
            </FlexBox>
          </FlexBox>
          <FlexBox direction="row">
            <FlexBox>
              <ToggleButtonBox name="Sidekick" 
                value={sidekick}
                disabled={ processing || !api.online } 
                onToggle={() => { setSidekick(!sidekick) }}
              />
            </FlexBox>
            <FlexBox>
              <ToggleButtonBox name="Unassigned" 
                value={unassigned}
                disabled={ processing || !api.online || hidden || details_hidden || add_existing } 
                onToggle={() => setUnassigned(!unassigned) }
              />
            </FlexBox>
            <FlexBox>
              <ToggleButtonBox name="Hidden" 
                value={hidden}
                disabled={ processing || !api.online || details_hidden || unassigned } 
                onToggle={() => setHidden(!hidden) }
              />
            </FlexBox>
            <FlexBox>
              <ToggleButtonBox name="Details Hidden" 
                value={details_hidden}
                disabled={ processing || !api.online || hidden || unassigned } 
                onToggle={() => setDetailsHidden(!details_hidden) }
              />
            </FlexBox>
          </FlexBox>
          { renderExisting() }
        </FlexBox>
      </ModalOrDrawer>
    );
  }

  const renderExisting = () => {
    if (add_existing) {
      return (
        <FlexBox direction="column">
          <FlexBox>
            Adding an existing character which doesn"t comply with the allowed options for this campaign will automatically undo those options on the character.<br/>
            This may include race, subrace, class, subclass, gifts, items, and spells.
          </FlexBox>
          <FlexBox>
            <Grid container spacing={0} direction="row">
              { my_characters.filter(o => o.sidekick === sidekick).map(char => {
                return (
                  <Grid size={4} key={char._id}>
                    <div className="padded">
                      <FlexBox direction="column" className={cx(classes.root)}>
                        <FlexBox direction="row">
                          <FlexBox className="padded">
                            <DisplayObject obj={char} />
                          </FlexBox>
                          { char.image_url !== "" &&
                            <FlexBox className="padded">
                              <img src={ char.image_url } alt="character image" 
                                style={{
                                  maxWidth: "35px",
                                  maxHeight: "35px",
                                  float: "right"
                                }}
                              />
                            </FlexBox>
                          }
                        </FlexBox>
                        <FlexBox>
                          <DandDButtonBox name="Join" 
                            disabled={ processing || !api.online } 
                            onClick={ async () => {
                              setProcessing(true);
                              const c_char = new CampaignChar();
                              c_char.campaign_id = campaign._id;
                              c_char.campaign = campaign;
                              c_char.char_id = char._id;
                              c_char.char = char;
                              c_char.hide_char = hidden;
                              c_char.hide_details = details_hidden;
                              await api.upsertObject("campaign_char", c_char);
                              campaign.c_chars.push(c_char);
                              char.c_char = c_char;
                              campaign.characters.push(char);
                              setAddExisting(false);
                              setProcessing(false);
                            }}
                          />
                        </FlexBox>
                      </FlexBox>
                    </div>
                  </Grid>
                );
              })}
            </Grid>
          </FlexBox>
          <FlexBox>
            <DandDButtonBox name="Cancel" 
              disabled={ processing }
              onClick={() => {
                setAddExisting(false);
              }}
            />
          </FlexBox>
        </FlexBox>
      );
    }
    return null;
  }

  if (navigateTo !== null) {
    return <Navigate to={navigateTo} />;
  } else {
    return (
      <FlexBox direction="column">
        { api.user && 
          <FlexBox direction="row">
            <FlexBox xs_expand={0} className="padded">
              <AddButton
                onClick={() => setAdding(true) }
              />
            </FlexBox>
            <FlexBox xs_expand={0} className="padded">
              <RefreshButton
                disabled={processing}
                onClick={ async () => {
                  setProcessing(true);
                  campaign.c_players = undefined;
                  campaign.c_chars = [];
                  await api.loadCPlayers({ campaign, include_players: true });
                  await api.loadCChars({ campaign, include_chars: true, include_players: true });
                  setProcessing(false);
                }}
              />
            </FlexBox>
          </FlexBox>
        }
        { !reloading && campaign.c_chars.filter(o => o.active).length > 0 &&
          <>
            <FlexBox className="header centered">Active Characters</FlexBox>
            <FlexBox>
              <Grid container spacing={0} direction="row">
                { renderCharacters(true) }
              </Grid>
            </FlexBox>
          </>
        }
        { !reloading && campaign.c_chars.filter(o => !o.active).length > 0 &&
          <>
            <FlexBox className="header centered">Inactive Characters</FlexBox>
            <FlexBox>
              <Grid container spacing={0} direction="row">
                { renderCharacters(false) }
              </Grid>
            </FlexBox>
          </>
        }
        { renderExtras() }
      </FlexBox>
    ); 
  }
}
